import { API_ENDPOINTS } from "@infrastructure/InfrastructureConstants";
import { HubConnection, HubConnectionBuilder, HttpTransportType } from "@microsoft/signalr";
import { runtimeConfig } from "@utils/runtimeConfig/runtimeConfig";

type RefreshListListener = () => void;
type RefreshJobListener = (jobId: string) => void;
type ProgressPayload = {
  jobId: string;
  batchId: string;
  progress: number;
};
type ProgressListener = (payload: ProgressPayload) => void;

const refreshListListeners = new Set<RefreshListListener>();
const refreshJobListeners = new Set<RefreshJobListener>();
const progressListeners = new Set<ProgressListener>();

const jobSubscriptions = new Map<string, number>();

const connectionState: { connection: HubConnection | null; startPromise: Promise<void> | null; jobListSubscriptions: number } = {
  connection: null,
  startPromise: null,
  jobListSubscriptions: 0,
};

const getConnectionUrl = () => runtimeConfig.API_BASE_URL.replace("/swagger", "") + API_ENDPOINTS.jobsHub;

const dispatchRefreshList = () => {
  refreshListListeners.forEach((listener) => listener());
};

const dispatchRefreshJob = (payload?: { jobId?: string }) => {
  if (!payload?.jobId) return;
  refreshJobListeners.forEach((listener) => listener(payload.jobId!));
};

const dispatchProgress = (payload?: Partial<ProgressPayload>) => {
  if (!payload?.jobId || !payload.batchId || payload.progress == undefined) return;

  const normalizedPayload: ProgressPayload = {
    jobId: payload.jobId,
    batchId: payload.batchId,
    progress: payload.progress,
  };

  progressListeners.forEach((listener) => listener(normalizedPayload));
};

const getOrCreateConnection = () => {
  if (connectionState.connection) {
    return connectionState.connection;
  }

  const connection = new HubConnectionBuilder()
    .withUrl(getConnectionUrl(), {
      transport: HttpTransportType.ServerSentEvents,
    })
    .withAutomaticReconnect()
    .build();

  connection.on("RefreshList", dispatchRefreshList);
  connection.on("RefreshJob", dispatchRefreshJob);
  connection.on("ReceiveProgress", dispatchProgress);

  connection.onreconnected(() => resubscribe());
  connection.onclose(() => {
    connectionState.startPromise = null;
  });

  connectionState.connection = connection;
  return connection;
};

const startConnection = async () => {
  const currentConnection = getOrCreateConnection();

  if (currentConnection.state === "Connected") {
    return;
  }

  if (connectionState.startPromise) {
    return connectionState.startPromise;
  }

  connectionState.startPromise = (async () => {
    try {
      await currentConnection.start();
    } catch (error) {
      console.error("Jobs hub connection error:", error);
      throw error;
    } finally {
      connectionState.startPromise = null;
    }
  })();

  return connectionState.startPromise;
};

const invokeWhenConnected = async (methodName: string, ...arguments_: unknown[]) => {
  await startConnection();
  await getOrCreateConnection().invoke(methodName, ...arguments_);
};

/**
 * Invokes a hub method only if a connection is already live, and never opens one.
 *
 * Teardown paths (unsubscribe) must not use `invokeWhenConnected`: it starts a connection, and the
 * caller then immediately runs `stopConnectionIfIdle()`, aborting the in-flight negotiation with
 * "The connection was stopped during negotiation". Skipping the call is safe because the server
 * drops all group memberships when the connection closes.
 */
const invokeIfConnected = async (methodName: string, ...arguments_: unknown[]) => {
  const currentConnection = connectionState.connection;
  if (!currentConnection || currentConnection.state !== "Connected") {
    return;
  }

  await currentConnection.invoke(methodName, ...arguments_);
};

const resubscribe = async () => {
  if (connectionState.jobListSubscriptions > 0) {
    await invokeWhenConnected("SubscribeToJobList");
  }

  for (const jobId of jobSubscriptions.keys()) {
    await invokeWhenConnected("SubscribeToJob", jobId);
  }
};

const stopConnectionIfIdle = () => {
  if (refreshListListeners.size > 0 || refreshJobListeners.size > 0 || progressListeners.size > 0 || connectionState.jobListSubscriptions > 0 || jobSubscriptions.size > 0) {
    return;
  }

  const currentConnection = connectionState.connection;
  connectionState.connection = null;
  connectionState.startPromise = null;

  if (!currentConnection) {
    return;
  }

  currentConnection.off("RefreshList", dispatchRefreshList);
  currentConnection.off("RefreshJob", dispatchRefreshJob);
  currentConnection.off("ReceiveProgress", dispatchProgress);
  void currentConnection.stop().catch((error) => {
    console.error("Error stopping jobs hub connection:", error);
  });
};

export const subscribeToJobsList = () => {
  connectionState.jobListSubscriptions += 1;
  if (connectionState.jobListSubscriptions === 1) {
    void invokeWhenConnected("SubscribeToJobList").catch((error) => {
      console.error("Error subscribing to jobs list:", error);
    });
  }

  return () => {
    connectionState.jobListSubscriptions = Math.max(0, connectionState.jobListSubscriptions - 1);
    if (connectionState.jobListSubscriptions === 0) {
      void invokeIfConnected("UnsubscribeFromJobList").catch((error) => {
        console.error("Error unsubscribing from jobs list:", error);
      });
    }

    stopConnectionIfIdle();
  };
};

export const subscribeToJob = (jobId: string) => {
  const nextCount = (jobSubscriptions.get(jobId) ?? 0) + 1;
  jobSubscriptions.set(jobId, nextCount);

  if (nextCount === 1) {
    void invokeWhenConnected("SubscribeToJob", jobId).catch((error) => {
      console.error(`Error subscribing to job ${jobId}:`, error);
    });
  }

  return () => {
    const currentCount = jobSubscriptions.get(jobId) ?? 0;

    if (currentCount <= 1) {
      jobSubscriptions.delete(jobId);
      void invokeIfConnected("UnsubscribeFromJob", jobId).catch((error) => {
        console.error(`Error unsubscribing from job ${jobId}:`, error);
      });
    } else {
      jobSubscriptions.set(jobId, currentCount - 1);
    }

    stopConnectionIfIdle();
  };
};

export const subscribeToRefreshList = (listener: RefreshListListener) => {
  refreshListListeners.add(listener);
  void startConnection().catch(() => {});

  return () => {
    refreshListListeners.delete(listener);
    stopConnectionIfIdle();
  };
};

export const subscribeToRefreshJob = (listener: RefreshJobListener) => {
  refreshJobListeners.add(listener);
  void startConnection().catch(() => {});

  return () => {
    refreshJobListeners.delete(listener);
    stopConnectionIfIdle();
  };
};

export const subscribeToProgress = (listener: ProgressListener) => {
  progressListeners.add(listener);
  void startConnection().catch(() => {});

  return () => {
    progressListeners.delete(listener);
    stopConnectionIfIdle();
  };
};
